import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "../../../language/i18n";
import { ApproveButton, CancelButton } from "../../../library";
import { duelSocialStyles } from "./duelSocialStyles";

// Polished, right-docked incoming-invite card: an accent medallion wrapped by a circular countdown
// ring (replacing the old thin progress bar), the inviter's name, and clear accept/decline actions.
// The ring turns urgent (coral) in the final seconds. Prefix: dso-.
interface IDuelInvite {
  inviteId: string;
  duelId: number | string;
  matchCode: string;
  kind: string;
  inviterName?: string;
  expiresAt?: number;
}

const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const TOTAL_MS = 30000;

const DuelInviteToast = (props: {
  invite: IDuelInvite;
  queueCount?: number;
  onAccept: () => void;
  onDecline: () => void;
  onExpire: () => void;
}) => {
  const { invite, queueCount = 1, onAccept, onDecline, onExpire } = props;
  // Deadline is captured once per invite (the toast is keyed by inviteId, so a
  // new invite remounts): server-provided expiresAt, or a client-side fallback
  // of the full TTL — never 0, so a missing expiresAt can't expire instantly.
  const deadlineRef = useRef(invite.expiresAt || Date.now() + TOTAL_MS);
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, deadlineRef.current - Date.now()),
  );
  const [busy, setBusy] = useState(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    const interval = setInterval(() => {
      const left = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(left);
      if (left <= 0) {
        clearInterval(interval);
        onExpireRef.current();
      }
    }, 250);
    return () => clearInterval(interval);
  }, []);

  const isSpectator = invite.kind === "SPECTATOR";
  const inviterName = invite.inviterName || i18n.t("a_friend");
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const frac = Math.max(0, Math.min(1, remainingMs / TOTAL_MS));
  const dashOffset = RING_CIRCUMFERENCE * (1 - frac);
  const urgent = remainingSeconds <= 8;

  const handleAccept = () => {
    if (busy) return;
    setBusy(true);
    onAccept();
  };
  const handleDecline = () => {
    if (busy) return;
    setBusy(true);
    onDecline();
  };

  return createPortal(
    <div className={"dso-toast" + (urgent ? " dso-toast--urgent" : "")} role="alert">
      <style>{duelSocialStyles}</style>
      <div className="dso-toast-main">
        <div className="dso-medallion">
          <svg className="dso-ring" viewBox="0 0 60 60" aria-hidden="true">
            <circle className="dso-ring-track" cx="30" cy="30" r={RING_RADIUS} />
            <circle
              className="dso-ring-prog"
              cx="30"
              cy="30"
              r={RING_RADIUS}
              style={{
                strokeDasharray: RING_CIRCUMFERENCE,
                strokeDashoffset: dashOffset,
              }}
            />
          </svg>
          <span className="dso-medallion-core">
            <span className="dso-medallion-emoji" aria-hidden="true">
              {isSpectator ? "👁️" : "⚔️"}
            </span>
          </span>
        </div>
        <div className="dso-toast-text">
          <span className="dso-toast-kicker">
            {isSpectator
              ? i18n.t("duel_spectate_invite_title")
              : i18n.t("duel_challenge_invite_title")}
            <span className="dso-toast-secs">{remainingSeconds}s</span>
            {queueCount > 1 ? (
              <span className="dso-toast-more">+{queueCount - 1}</span>
            ) : null}
          </span>
          <p className="dso-toast-msg">
            <b>{inviterName}</b>{" "}
            {isSpectator
              ? i18n.t("duel_spectate_invite_tail")
              : i18n.t("duel_challenge_invite_tail")}
          </p>
        </div>
      </div>
      <div className="dso-toast-actions">
        <CancelButton
          title={i18n.t("decline")}
          size="small"
          disabled={busy}
          onClick={handleDecline}
        />
        <ApproveButton
          title={isSpectator ? i18n.t("watch") : i18n.t("accept_and_play")}
          size="small"
          disabled={busy}
          onClick={handleAccept}
        />
      </div>
    </div>,
    document.body,
  );
};

export default DuelInviteToast;
